/**
 * Route-level permission map — checked by the `_app` layout route's
 * `beforeLoad` guard.
 *
 * Each entry maps a URL path prefix to the permission(s) required.
 * The list is checked in order — first match wins — so more specific
 * patterns MUST come before general ones (e.g. `/catalog/bom` before
 * `/catalog`).
 *
 * Semantics mirror the sidebar: when `requires` is an array the user
 * needs at least ONE of the listed permissions (OR logic).
 */

import type { PermissionKey } from "@bizop/shared/rbac";

type RoutePermission = {
  /** Path prefix to match (startsWith). */
  pattern: string;
  /** Permission(s) required — OR semantics (need at least one). */
  requires: PermissionKey | PermissionKey[];
};

/**
 * Route permission map.  Checked in order — first match wins.
 * More specific patterns come before general ones.
 */
export const ROUTE_PERMISSIONS: RoutePermission[] = [
  // ── People & Admin ──────────────────────────────────────────
  { pattern: "/people/roles", requires: "roles.write" },
  { pattern: "/people/users", requires: "users.read" },

  // ── Finance ─────────────────────────────────────────────────
  { pattern: "/finance/reports", requires: "reports.financial" },
  { pattern: "/finance/gl", requires: "accounts.read" },
  { pattern: "/finance/fx", requires: "fx.write" },
  { pattern: "/finance/loans", requires: "loans.write" },
  { pattern: "/finance/accounts", requires: "accounts.read" },
  { pattern: "/finance/payables", requires: "finance.read" },
  { pattern: "/finance/expenses", requires: "finance.read" },
  { pattern: "/finance", requires: "finance.read" },

  // ── Catalog ─────────────────────────────────────────────────
  { pattern: "/catalog/bom", requires: "boms.read" },
  { pattern: "/catalog", requires: "products.read" },

  // ── Inventory ───────────────────────────────────────────────
  { pattern: "/inventory/warehouses", requires: "inventory.read" },
  { pattern: "/inventory/transfers", requires: "inventory.read" },
  { pattern: "/inventory", requires: "inventory.read" },

  // ── Imports ─────────────────────────────────────────────────
  { pattern: "/imports/lc", requires: ["lc.read", "lc.write"] },
  { pattern: "/imports/purchase-orders", requires: "purchaseOrders.read" },
  { pattern: "/imports/suppliers", requires: "suppliers.read" },
  { pattern: "/imports/shipments", requires: "shipments.read" },

  // ── Sales ───────────────────────────────────────────────────
  { pattern: "/sales/orders", requires: "salesOrders.read" },
  { pattern: "/sales/invoices", requires: "invoices.read" },
  { pattern: "/sales/payments", requires: ["payments.record", "invoices.read"] },
  { pattern: "/sales/dealers", requires: "dealers.read" },
  { pattern: "/sales/credit-notes", requires: "invoices.read" },
  { pattern: "/sales/returns", requires: "invoices.read" },
  { pattern: "/sales/deliveries", requires: "salesOrders.read" },

  // ── Pricing ─────────────────────────────────────────────────
  { pattern: "/pricing", requires: "pricing.read" },

  // ── Workforce ───────────────────────────────────────────────
  { pattern: "/workforce/payroll", requires: "payroll.read" },
  { pattern: "/workforce/attendance", requires: "attendance.read" },
  { pattern: "/workforce/employees", requires: "employees.read" },

  // ── Production ──────────────────────────────────────────────
  { pattern: "/production", requires: "boms.read" },

  // ── Settings ────────────────────────────────────────────────
  { pattern: "/settings/opening-balances", requires: ["opening.read", "opening.write", "settings.write"] },
  { pattern: "/settings/dealer-areas", requires: ["dealerAreas.read", "settings.write", "users.read"] },
  { pattern: "/settings/branches", requires: ["branches.read", "settings.write", "users.read"] },
  { pattern: "/settings/tax-rates", requires: ["taxRates.write", "settings.write"] },
  { pattern: "/settings/units", requires: ["units.write", "settings.write"] },
  { pattern: "/settings/brands", requires: "settings.write" },
  { pattern: "/settings/categories", requires: "settings.write" },
  { pattern: "/settings/master-data", requires: ["settings.write", "users.read"] },
  { pattern: "/settings/import-export", requires: "settings.write" },

  // ── Analysis ────────────────────────────────────────────────
  { pattern: "/analysis", requires: "finance.read" },

  // ── Reports ─────────────────────────────────────────────────
  { pattern: "/reports", requires: "reports.read" },

  // ── Audit ───────────────────────────────────────────────────
  { pattern: "/audit", requires: "audit.read" },
];

/**
 * Check if a path requires permissions the user doesn't have.
 * Returns `true` if access is allowed, `false` if denied.
 */
export function isRouteAllowed(
  pathname: string,
  permissions: PermissionKey[],
): boolean {
  for (const route of ROUTE_PERMISSIONS) {
    if (pathname.startsWith(route.pattern)) {
      const required = Array.isArray(route.requires)
        ? route.requires
        : [route.requires];
      // OR semantics — need at least one
      return required.some((p) => permissions.includes(p));
    }
  }
  // No matching pattern → allowed (dashboard, notifications, settings index, etc.)
  return true;
}
